import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  CardMedia,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { Bookmark as BookmarkIcon } from "../icons";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { auth } from "../firebase";
import { isLoggedIn } from "../session";
import { T } from "./Translate";
import BackButton from "./BackButton";
import BottomToolbar from "./BottomToolbar";
import {
  fetchSavedOfferIds,
  fetchOffersByIds,
  fetchHelpersInfo,
  toggleSaveOffer,
  getOfferPricing,
} from "./homeUtils";
import {
  fetchSavedEventIds,
  fetchEventsByIds,
  toggleSaveEvent,
  formatDate,
} from "./eventsUtils";
import { barColors } from "../theme";
import { LISTING_TYPES, fetchSavedListingIds, fetchListingsByIds, toggleSaveListing } from "../utils/listingSocial";

// Autos y propiedades (ver utils/listingSocial.js): chip y ruta de cada tipo.
const LISTING_META = {
  car: { Icon: DirectionsCarIcon, label: "Auto" },
  place: { Icon: FlightIcon, label: "Propiedad" },
};

// Lista privada de ofertas y eventos guardados por el usuario (menú personal
// en profile.js), separada del perfil público ("Perfil" con sus tabs de
// Ofertas/Eventos/Tiendas): no hay una página propia por oferta/evento en la
// app, así que cada item linkea al perfil de la empresa que lo publicó.
const SavedItems = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savedItems, setSavedItems] = useState([]);
  const [helpersInfo, setHelpersInfo] = useState({});

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const load = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }
      const uid = auth.currentUser.uid;
      const [offerIds, eventIds, carIds, placeIds] = await Promise.all([
        fetchSavedOfferIds(uid),
        fetchSavedEventIds(uid),
        fetchSavedListingIds("car", uid),
        fetchSavedListingIds("place", uid),
      ]);
      const [offersData, eventsData, carsData, placesData] = await Promise.all([
        fetchOffersByIds(offerIds),
        fetchEventsByIds(eventIds),
        fetchListingsByIds("car", carIds),
        fetchListingsByIds("place", placeIds),
      ]);

      const combined = [
        ...offersData.map((offer) => ({ ...offer, itemType: "offer" })),
        ...eventsData.map((event) => ({ ...event, itemType: "event" })),
        ...carsData.map((car) => ({ ...car, itemType: "car" })),
        ...placesData.map((place) => ({ ...place, itemType: "place" })),
      ];
      setSavedItems(combined);
      setHelpersInfo(await fetchHelpersInfo(combined.map((item) => item.uid)));
      setLoading(false);
    };
    load();
  }, []);

  const handleUnsave = (item) => {
    if (!auth.currentUser) return;
    if (LISTING_TYPES[item.itemType]) {
      toggleSaveListing(item.itemType, auth.currentUser.uid, item.id, true);
    } else if (item.itemType === "event") {
      toggleSaveEvent(auth.currentUser.uid, item.id, true);
    } else {
      toggleSaveOffer(auth.currentUser.uid, item.id, true);
    }
    setSavedItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  return (
    <Box>
      <AppBar position="fixed" sx={{ backgroundColor: barColors.background, color: barColors.text }}>
        <Toolbar sx={{ display: "flex", justifyContent: "center", p: "10px", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
            <BackButton color="inherit" />
            <Typography variant="h6" sx={{ ml: 2, color: barColors.text }}>
              <T>Guardados</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: "90px", mb: 14, maxWidth: { xs: "100%", md: "980px" } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : savedItems.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            <T>No guardaste ninguna publicación todavía.</T>
          </Typography>
        ) : (
          <List>
            {savedItems.map((item) => {
              const isEvent = item.itemType === "event";
              const listing = LISTING_META[item.itemType];
              const pricing = !isEvent && !listing ? getOfferPricing(item) : null;
              const image = Array.isArray(item.images) ? item.images[0] : item.images;
              const displayPrice = pricing ? pricing.finalPrice : Number(item.price);
              const helper = helpersInfo[item.uid];
              return (
                <ListItem
                  key={item.id}
                  component={Link}
                  to={listing ? `${LISTING_TYPES[item.itemType].path}/${item.id}` : `/profile/${item.uid}`}
                  sx={{
                    borderRadius: 2,
                    mb: 1.5,
                    p: 1.5,
                    backgroundColor: (theme) => theme.palette.background.paper,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    boxShadow: 1,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnsave(item);
                      }}
                      aria-label="quitar de guardados"
                    >
                      <BookmarkIcon sx={{ color: "text.primary" }} />
                    </IconButton>
                  }
                >
                  {image ? (
                    <CardMedia
                      component="img"
                      image={image}
                      alt={item.product || item.title}
                      sx={{ width: 56, height: 56, borderRadius: 1.5, mr: 2, objectFit: "cover" }}
                    />
                  ) : (
                    <ListItemAvatar>
                      <Avatar src={helper?.profileImageUrl} />
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {item.product || item.title}
                        <Chip
                          size="small"
                          icon={listing ? <listing.Icon /> : isEvent ? <EventIcon /> : <LocalOfferIcon />}
                          label={listing ? <T>{listing.label}</T> : isEvent ? <T>Evento</T> : <T>Oferta</T>}
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      </Box>
                    }
                    secondary={
                      isEvent ? (
                        <>
                          {helper?.name || <T>Usuario</T>}
                          {item.date && ` · ${formatDate(item.date)}`}
                        </>
                      ) : (
                        <>
                          {helper?.name || <T>Usuario</T>}
                          {Number.isFinite(displayPrice) && ` · $${displayPrice.toFixed(2)}`}
                        </>
                      )
                    }
                    primaryTypographyProps={{ fontWeight: "bold" }}
                    sx={{ pr: 5 }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Container>
      <BottomToolbar />
    </Box>
  );
};

export default SavedItems;
